// =============================================================================
// MeuDriver — useInstructorSubscription
// Hook para gerenciar a assinatura Pro do instrutor.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { InstructorSubscriptionRow } from '@/types/database'
import { toast } from 'sonner'

const instructorSubKeys = {
  instructor: (userId: string) => ['subscription', 'instructor', userId] as const,
}

async function fetchInstructorSubscription(
  userId: string,
): Promise<InstructorSubscriptionRow | null> {
  const { data, error } = await supabase
    .from('instructor_subscriptions')
    .select('*')
    .eq('instructor_profile_id', userId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data as InstructorSubscriptionRow | null
}

/** Retorna a assinatura Pro ativa do instrutor autenticado, ou null. */
export function useInstructorSubscription() {
  const { user } = useAuth()

  return useQuery<InstructorSubscriptionRow | null, Error>({
    queryKey: instructorSubKeys.instructor(user?.id ?? ''),
    queryFn: () => fetchInstructorSubscription(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
  })
}

/** Inicia o fluxo de assinatura Pro (redireciona para o Mercado Pago). */
export function useCreateInstructorSubscription() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<{ init_point: string }, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ init_point: string }>(
        'create-instructor-subscription',
        { body: { plan: 'pro' } },
      )
      if (error) throw new Error(error.message)
      if (!data?.init_point) throw new Error('init_point ausente na resposta')
      return data
    },
    onSuccess: (data) => {
      window.location.href = data.init_point
    },
    onError: (err) => {
      console.error('[useCreateInstructorSubscription]', err)
      toast.error('Erro ao iniciar assinatura Pro. Tente novamente.')
    },
    onSettled: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: instructorSubKeys.instructor(user.id) })
      }
    },
  })
}

/** Cancela a assinatura Pro do instrutor via MP + DB. */
export function useCancelInstructorSubscription() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      // Reutiliza a edge function genérica de cancel para instrutores
      // (seria criar cancel-instructor-subscription — aqui chamamos direto o DB
      // e cancelamos via MP através de uma função compartilhada futura;
      // por ora, invoca cancel-subscription que resolve pelo user_id)
      const { error } = await supabase.functions.invoke('cancel-instructor-subscription', { body: {} })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Assinatura Pro cancelada com sucesso.')
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: instructorSubKeys.instructor(user.id) })
      }
    },
    onError: (err) => {
      console.error('[useCancelInstructorSubscription]', err)
      toast.error('Erro ao cancelar assinatura Pro. Tente novamente.')
    },
  })
}
